import type { ComponentProps } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import moment from 'moment'
import {
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Typography
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import Content from 'components/content/content'
import Filter from 'components/filter/filter'
import {
  useDeleteBannerMutation,
  useGetListBannerEventMutation
} from 'store/serviceBookingEventBanner'
import Icon from 'components/icon'
import CONSTANT from 'modules/constant'
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import type { BannerPlacement, ErrorProps, ServiceBookingEventBannerProps } from 'modules/types'
import type { PartialBanner } from 'modules/partial'
import MenuModalItem from 'components/menuItem/menuItem'
import GlobalStyle from 'modules/styles'
import Create from './create/create'
import Edit from './edit/page'
import { BANNER_PLACEMENT_POLICY } from './config'
import type { BannerView } from './config'

type BannerFilter = Pick<PartialBanner, 'Status' | 'MaxValidFrom' | 'MaxValidUntil'>

const Banner = ({
  module = 'service-event',
  placement = 'HomePage'
}: {
  module?: 'service-event' | 'banner'
  placement?: BannerPlacement
}) => {
  const view: BannerView = module === 'banner'
    ? { kind: 'banner', placement }
    : { kind: 'service-event', placement: 'ServiceAccessories' }
  return <BannerList key={placement} view={view} showEventSelector={module === 'service-event'} />
}

export { Banner }

const BannerList = ({ view, showEventSelector }: {
  view: BannerView
  showEventSelector: boolean
}) => {
  const [getBannerEventList, bannerEventList] = useGetListBannerEventMutation()
  const placement = view.placement
  const placementPolicy = BANNER_PLACEMENT_POLICY[placement]
  const isCustomerCare = view.kind === 'banner' && placement === 'CustomerCare'

  const [payload, setPayload] = useState<PartialBanner>({
    start: 0,
    length: placementPolicy.pageSize ?? CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    field: CONSTANT.DEFAULT_CAR_INFORMATION_FIELD.join(','),
    Search: '',
    Event: '',
    BannerImage: '',
    Vin: '',
    MaxValidFrom: '',
    MaxValidUntil: '',
    Status: ''
  })
  const [filter, setFilter] = useState<BannerFilter>({
    Status: '',
    MaxValidFrom: '',
    MaxValidUntil: ''
  })

  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [menu, setMenu] = useState<null | number>(null)
  const [dialogFilter, setDialogFilter] = useState(false)
  const [dialogCreate, setDialogCreate] = useState<boolean>(false)
  const [dialogUpdate, setDialogUpdate] = useState<boolean>(false)
  const [setDeleteBanner, deleteBanner] = useDeleteBannerMutation()

  const formatDate = (e: string) => (!isEmpty(e) ? moment(e).format('DD-MM-YYYY HH:mm') : '-')
  const onSearch = (value: string) =>
    setPayload((current) => ({ ...current, start: 0, Search: value }))
  const onChangePage = (value: number) =>
    setPayload((current) => ({ ...current, start: value - 1 }))
  const onChangePerPage = (value: number) =>
    setPayload((current) => ({ ...current, length: placementPolicy?.pageSize ?? value }))

  const onAction = (event: React.MouseEvent<HTMLElement>, index: null | number) => {
    setAnchor(event.currentTarget)
    setMenu(index)
  }

  const onRemoveAnchor = async (callback?: CallableFunction) => {
    setAnchor(null)
    callback && callback(callback)
  }

  const onResync = useCallback(() => {
    const formData = new FormData()

    formData.append('start', payload.start.toString())
    formData.append('length', payload.length.toString())
    formData.append('field', payload.field)

    if (view.kind === 'service-event') {
      formData.append('IsEvent', 'true')
    } else {
      formData.append('Placement', placement)
    }

    if (!isEmpty(payload.Search)) formData.append('Search', payload.Search)
    if (!isEmpty(payload.Event)) formData.append('Event', payload.Event)
    if (!isEmpty(payload.BannerImage)) formData.append('BannerImage', payload.BannerImage)
    if (!isEmpty(payload.MaxValidFrom)) formData.append('MaxValidFrom', payload.MaxValidFrom)
    if (!isEmpty(payload.MaxValidUntil)) formData.append('MaxValidUntil', payload.MaxValidUntil)
    if (!isEmpty(payload.Status)) formData.append('Status', payload.Status)
    getBannerEventList(formData)
  }, [getBannerEventList, payload, placement, view.kind])

  const columns = [
    ...[
      view.kind === 'service-event'
        ? {
            column: 'Event',
            label: 'Event',
            key: 'eventName'
          }
        : {
            column: 'Name',
            label: 'Name',
            key: 'title',
            render: (row: ServiceBookingEventBannerProps) => row.title || row.eventName || '-'
          }
    ],
    {
      column: 'Banner Image',
      label: 'Banner Image',
      key: 'bannerImage',
      render: (row: ServiceBookingEventBannerProps) => {
        const image = row.bannerImage.split('?')[0]
        return image ? image.split('/').slice(4).join('/') : '-'
      }
    },
    {
      column: 'URL',
      label: 'URL',
      key: 'url'
    },
    {
      column: 'Valid From',
      label: 'Period Start',
      key: 'validFrom',
      render: (row: ServiceBookingEventBannerProps) => formatDate(row.validFrom) || '-'
    },
    {
      column: 'Valid Until',
      label: 'Period End',
      key: 'validUntil',
      render: (row: ServiceBookingEventBannerProps) => formatDate(row.validUntil) || '-'
    },
    {
      column: 'Sequence',
      label: 'Sequence',
      key: 'sequence',
      render: (row: ServiceBookingEventBannerProps) => row.sequence
    },
    {
      column: 'Status',
      label: 'Status',
      key: 'status',
      render: (row: ServiceBookingEventBannerProps) => {
        let status = row.status
        let color: ComponentProps<typeof Chip>['color'] =
          row.status === 'Enabled' ? 'success' : 'error'

        if (dayjs(row.validFrom).isAfter(Date.now())) {
          status = 'Not Started'
          color = 'info'
        }

        if (dayjs(row.validUntil).isBefore(Date.now())) {
          status = 'Expired'
          color = 'default'
        }

        return <Chip label={status} color={color} />
      }
    },
    {
      column: 'View Count',
      label: 'View Count',
      key: 'viewCount',
      render: (row: ServiceBookingEventBannerProps) => row.viewCount ?? 0
    },
    {
      column: 'Click Count',
      label: 'Click Count',
      key: 'clickCount',
      render: (row: ServiceBookingEventBannerProps) => row.clickCount ?? 0
    },
    {
      column: 'Created At',
      label: 'Created At',
      key: 'createdAt',
      render: (row: ServiceBookingEventBannerProps) => formatDate(row.createdAt) || '-'
    }
  ].filter(({ key }) =>
    !isCustomerCare || !['url', 'validFrom', 'validUntil', 'sequence', 'clickCount'].includes(key)
  )

  const tableHeadTitles = columns.map((c) => c.label)

  const totalPage = bannerEventList.data && GLOBAL.tableTotalPage(bannerEventList.data.recordsTotal)
  const numbers = GLOBAL.tableNumber(payload.start)
  const openAnchor = Boolean(anchor)
  const canCreate = placementPolicy.canCreate === 'many' || (
    placementPolicy.canCreate === 'once' &&
    isCustomerCare &&
    !bannerEventList.isLoading &&
    bannerEventList.isSuccess &&
    bannerEventList.data?.recordsTotal === 0
  )

  useEffect(() => {
    onResync()
  }, [onResync])

  return (
    <>
      <Content
        title='Banner'
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        onFilter={!isCustomerCare ? () => setDialogFilter(true) : undefined}
        onAdditional={canCreate ? () => setDialogCreate(true) : undefined}
        additionalTitle={canCreate ? 'Create Banner' : undefined}
        additionalPrefixIcon={canCreate ? 'Create' : undefined}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        usePagination={!isCustomerCare}
        totalPage={totalPage}
        totalRecords={bannerEventList.data && bannerEventList.data.recordsTotal}
        page={payload.start + 1}
        isLoading={bannerEventList.isLoading}
      >
        {bannerEventList.isSuccess &&
          bannerEventList.data &&
          bannerEventList.data.data?.map((table, index) => (
            <TableRow key={index}>
              <TableCell>{numbers[index] + 1}</TableCell>
              <TableCell>
                <IconButton
                  id={`button-${index}-${table.id}`}
                  aria-haspopup='true'
                  aria-controls={openAnchor ? `menu-${index}-${table.id}` : undefined}
                  aria-expanded={openAnchor ? 'true' : undefined}
                  onClick={(event) => onAction(event, index)}
                >
                  <Icon icon='MoreHoriz' />
                </IconButton>
                <Menu
                  id={`menu-${index}-${table.id}`}
                  aria-labelledby={`button-${index}-${table.id}`}
                  anchorEl={anchor}
                  open={openAnchor && menu == index}
                  onClose={() => onRemoveAnchor(() => setMenu(null))}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                >
                  <MenuItem onClick={() => onRemoveAnchor(() => setDialogUpdate(true))}>
                    Edit
                  </MenuItem>
                  {placementPolicy.canDelete !== false && (
                    <MenuModalItem
                      title='Delete'
                      loading={deleteBanner.isLoading}
                      onClick={() => onRemoveAnchor(() => setDeleteBanner(table.id))}
                    >
                      <Typography {...GlobalStyle.ErrorTypography}>Delete</Typography>
                    </MenuModalItem>
                  )}
                </Menu>
                <Edit
                  id={table.id}
                  view={view}
                  showEventSelector={showEventSelector}
                  title={'Edit'}
                  open={dialogUpdate && menu == index}
                  onClose={() => setDialogUpdate(false)}
                />
              </TableCell>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {col.render ? col.render(table) : table[col.key as keyof typeof table] || '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </Content>

      {!isCustomerCare && (
        <Filter
          open={dialogFilter}
          title='Filter'
          onClose={() => setDialogFilter(false)}
          onCancel={() => setDialogFilter(false)}
          isSubmitDisabled={!GLOBAL.isFilterContainValue(filter)}
          onSubmit={() => {
            setPayload((current) => ({ ...current, ...filter, start: 0 }))
            setDialogFilter(false)
          }}
          onReset={() => {
            const emptyFilter: BannerFilter = {
              Status: '',
              MaxValidFrom: '',
              MaxValidUntil: ''
            }
            setFilter(emptyFilter)
            setPayload((current) => ({ ...current, ...emptyFilter, start: 0 }))
            setDialogFilter(false)
          }}
        >
          <FormControl fullWidth>
            <InputLabel id='banner-filter-status-label'>Status</InputLabel>
            <Select
              labelId='banner-filter-status-label'
              id='banner-filter-status'
              value={filter.Status}
              label='Status'
              onChange={(event) =>
                setFilter((current) => ({ ...current, Status: event.target.value }))
              }
            >
              <MenuItem value='Enabled'>Enabled</MenuItem>
              <MenuItem value='Disabled'>Disabled</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
            <DatePicker
              value={filter.MaxValidFrom ? dayjs(filter.MaxValidFrom) : null}
              onChange={(date: Dayjs | null) =>
                setFilter((current) => ({
                  ...current,
                  MaxValidFrom: date?.format('YYYY-MM-DD') || ''
                }))
              }
              label='Max Valid From'
              format='DD-MM-YYYY'
              sx={{ width: '100%' }}
            />
            <DatePicker
              value={filter.MaxValidUntil ? dayjs(filter.MaxValidUntil) : null}
              onChange={(date: Dayjs | null) =>
                setFilter((current) => ({
                  ...current,
                  MaxValidUntil: date?.format('YYYY-MM-DD') || ''
                }))
              }
              label='Max Valid Until'
              format='DD-MM-YYYY'
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
        </Filter>
      )}

      {canCreate && (
        <Create
          open={dialogCreate}
          view={view}
          showEventSelector={showEventSelector}
          onClose={() => setDialogCreate(false)}
        />
      )}
      <Notification
        open={!deleteBanner.isLoading && !deleteBanner.isUninitialized}
        onClose={() => (deleteBanner.isError ? deleteBanner.reset() : location.reload())}
        isError={Boolean(deleteBanner.error) && deleteBanner.isError}
        message={GLOBAL.returnExceptionMessage(
          deleteBanner.isError,
          deleteBanner.error as ErrorProps
        )}
      />
    </>
  )
}

export default Banner
